import { useCurrentAccount } from '@application/api';
import { useRouter } from '@application/utils';
import { CountryCode } from '@gql/graphql';
import {
  Button,
  CardItemBreed,
  CardItemBreeder,
  Container,
  DetailList,
  HeaderProfile,
  Section,
  SectionCardItemSlider,
  SectionTitle,
  SkeletonBox,
  Slideshow,
} from '@presentation/components';
import { ConnectionTag } from '@presentation/components/ContactInfo/components/ConnectionTag';
import { useTranslation } from '@presentation/utils';
import type { FC } from 'react';
import { useMemo } from 'react';
import * as Styled from './styled';
import type { StudPageProps } from './types';

export const StudPage: FC<StudPageProps> = (props) => {
  const {
    studLoading,
    studData,
    slideshowData,
    contactInfoData,
    breedCardData,
    breederItemsLoading,
    breederItems = [],
    moreBreedersLink,
  } = props;

  const { routeLocationDescriptor } = useRouter();
  const { translate } = useTranslation();
  const { currentAccountType } = useCurrentAccount();

  const breederCards = useMemo(() => {
    if (breederItemsLoading) {
      return Array(4)
        .fill(undefined)
        .map((_, index) => <CardItemBreeder style={{ height: 375 }} key={index} loading />);
    }
    return breederItems.map((item, index) => (
      <CardItemBreeder style={{ height: 375 }} key={index} {...item} />
    ));
  }, [breederItems, breederItemsLoading]);

  return (
    <>
      <HeaderProfile
        title={
          studLoading ? (
            <SkeletonBox style={{ width: '20%', height: '30px' }} />
          ) : (
            translate('studDetails.title')
              .replace('$name', studData?.breedName ?? '')
              .replace('$country', translate(`enum.country.${studData?.country ?? CountryCode.BE}`))
          )
        }
        backLink={routeLocationDescriptor('studList')}
      />

      <Container>
        <Styled.TopWrapper>
          <Styled.TopFlexWrapper>
            <Styled.TopSlideshowWrapper>
              {!!slideshowData && <Slideshow aspectRatio={4 / 3} {...slideshowData} />}
            </Styled.TopSlideshowWrapper>
            <Styled.TopDetailsWrapper>
              {studData?.details && (
                <DetailList
                  items={studData.details.map((detail) => ({
                    label: detail.label,
                    value: detail.value,
                  }))}
                />
              )}
              {contactInfoData?.showSendMessageButton && (
                <Styled.TopButtonsContainer>
                  <Button color="blue" outlined onClick={contactInfoData?.onSendMessageClick}>
                    {translate('common.buttons.sendMessage')}
                  </Button>
                </Styled.TopButtonsContainer>
              )}
            </Styled.TopDetailsWrapper>
          </Styled.TopFlexWrapper>
        </Styled.TopWrapper>

        {!!breedCardData && (
          <Section>
            <Styled.DescriptionContainer>
              <div>
                <SectionTitle>{translate('glossary.breed')}</SectionTitle>
                <Styled.BreedCardWrapper>
                  <CardItemBreed {...breedCardData} />
                </Styled.BreedCardWrapper>
              </div>
              <div>
                <SectionTitle>{translate('glossary.description')}</SectionTitle>
                <Section>{studData?.description}</Section>
              </div>
            </Styled.DescriptionContainer>
          </Section>
        )}

        <Section>
          <Styled.ContactContainer>
            <Styled.ContactImgWrapper>
              <Styled.ContactImg
                src={contactInfoData?.profileImage}
                onContextMenu={(e) => e.preventDefault()}
              />
              {!!contactInfoData?.lastConnectedType && (
                <ConnectionTag type={contactInfoData.lastConnectedType} />
              )}
              {currentAccountType === 'breeder' && (
                <Styled.ContactLink onClick={contactInfoData?.onClick}>
                  {translate('common.buttons.viewProfile')}
                  {' >'}
                </Styled.ContactLink>
              )}
            </Styled.ContactImgWrapper>
            <Styled.ContactDetailList
              items={(contactInfoData?.values ?? []).map((value) => ({
                label: value.label,
                value: value.value ?? '-',
              }))}
            />
          </Styled.ContactContainer>
        </Section>

        <Styled.VerticalMargin>
          <SectionCardItemSlider
            title={translate('studDetails.relatedBreeders').replace(
              '$breed',
              studData?.breedName ?? '',
            )}
            loading={breederItemsLoading}
            items={breederCards}
            showMoreLink={moreBreedersLink}
          />
        </Styled.VerticalMargin>
      </Container>
    </>
  );
};
